import { useCallback, useEffect, useRef, type Ref, type RefObject } from 'react'

/** The popup's look: Prism's dialog surface, and its open and close motion. */
export const DIALOG_POPUP_CLASS_NAME = [
  'bg-background-overlay text-foreground corner-squircle shadow-xl fixed left-1/2 top-1/2 flex max-h-[calc(100vh-2rem)] w-[min(440px,calc(100%-2rem))] -translate-x-1/2 -translate-y-1/2 flex-col rounded-[var(--radius-2xl)] outline-none',
  '[transition:opacity_150ms_cubic-bezier(0.22,1,0.36,1),transform_240ms_cubic-bezier(0.22,1,0.36,1)]',
  'data-[ending-style]:scale-[0.94] data-[starting-style]:scale-[0.94] data-[ending-style]:opacity-0 data-[starting-style]:opacity-0',
  'motion-reduce:data-[ending-style]:scale-100 motion-reduce:data-[starting-style]:scale-100',
].join(' ')

const EASE_MS = 200
const EASE_CURVE = 'cubic-bezier(0.22, 1, 0.36, 1)'

/**
 * Password managers append fixed-position UI to an open dialog. Measuring from it sends the ease to the
 * wrong target. A `display: contents` wrapper (Interactive) has no box, so its children stand in for it.
 */
function lastInFlowChild(parent: HTMLElement): HTMLElement | null {
  for (let child = parent.lastElementChild; child; child = child.previousElementSibling) {
    const { display, position } = getComputedStyle(child)
    if (display === 'none' || position === 'absolute' || position === 'fixed') {
      continue
    }
    if (display === 'contents') {
      const inner = lastInFlowChild(child as HTMLElement)
      if (inner) {
        return inner
      }
      continue
    }
    return child as HTMLElement
  }
  return null
}

/** The height the content wants, read off its last in-flow child, which lays out at its natural place mid-ease. */
function naturalHeight(popup: HTMLElement): number {
  const last = lastInFlowChild(popup)
  if (!last) {
    return popup.offsetHeight
  }
  const style = getComputedStyle(popup)
  const natural =
    last.offsetTop +
    last.offsetHeight +
    parseFloat(getComputedStyle(last).marginBottom) +
    parseFloat(style.paddingBottom)
  const max = parseFloat(style.maxHeight)
  return Number.isFinite(max) ? Math.min(natural, max) : natural
}

/**
 * Eases the popup between heights when its content changes (a step folding, an error line, a picker
 * coming in), retargeting if the content changes again mid-ease. Not on opening and not under reduced motion.
 */
export function useHeightEasing(ref: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const popup = ref.current
    if (!popup) {
      return
    }
    const still = matchMedia('(prefers-reduced-motion: reduce)')
    let settled: number | null = null
    let run: { anim: Animation; to: number } | null = null

    const observer = new ResizeObserver(() => {
      const natural = run ? naturalHeight(popup) : popup.offsetHeight
      const target = run ? run.to : settled
      if (target === null) {
        settled = natural
        return
      }
      if (Math.abs(natural - target) < 1) {
        return
      }
      settled = natural
      if (still.matches) {
        return
      }
      const from = run ? popup.offsetHeight : target
      run?.anim.cancel()
      popup.style.overflow = 'clip'
      const anim = popup.animate([{ height: `${from}px` }, { height: `${natural}px` }], {
        duration: EASE_MS,
        easing: EASE_CURVE,
      })
      run = { anim, to: natural }
      anim.finished.then(
        () => {
          run = null
          popup.style.overflow = ''
          settled = popup.offsetHeight
        },
        () => {},
      )
    })
    observer.observe(popup)
    return () => observer.disconnect()
  }, [ref])
}

/**
 * The popup takes focus itself, so a screen reader reads the title before the first control, unless a
 * field exists to be typed into and marks itself `data-autofocus`.
 */
export function useAutofocusTarget(ref: RefObject<HTMLElement | null>) {
  return useCallback(() => ref.current?.querySelector<HTMLElement>('[data-autofocus]') ?? ref.current, [ref])
}

/** Local ref for the popup's behaviours, kept in step with the caller's forwarded ref. */
export function usePopupRef(forwarded: Ref<HTMLDivElement>) {
  const ref = useRef<HTMLDivElement | null>(null)
  const setRef = useCallback(
    (node: HTMLDivElement | null) => {
      ref.current = node
      if (typeof forwarded === 'function') {
        forwarded(node)
      } else if (forwarded) {
        ;(forwarded as { current: HTMLDivElement | null }).current = node
      }
    },
    [forwarded],
  )
  return { ref, setRef }
}
